import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/Loading';
import { DELIVERY_STATUS_LABEL, pastorApi, errorMessage } from '../pastorApi';
import type { JobRecruit, JobRequest } from '../pastorApi';
import { usePastorMe } from '../usePastorMe';

// 담임목사 청빙 소식 — 구인정보의 담임 공고 + (이력서 등록 사역자에게만) 사역자모아에 들어온 청빙 의뢰
export default function PastorJobs() {
  const navigate = useNavigate();
  const { isLogin, me } = usePastorMe();
  const [data, setData] = useState<{ recruits: JobRecruit[]; requests: JobRequest[] } | null>(null);
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    pastorApi.jobs().then(setData).catch(() => setData({ recruits: [], requests: [] }));
  }, []);
  useEffect(load, [load, me]);

  const interest = async (id: number) => {
    setBusy(true);
    try {
      await pastorApi.interest(id, note);
      setNoteFor(null);
      setNote('');
      alert('사역자모아가 확인한 뒤 교회에 목사님의 이력서를 전달해 드리겠습니다.');
      load();
    } catch (e) {
      alert(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const go = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  if (!data) return <div className="pastor-call inner pc-page"><Loading /></div>;
  const hasProfile = Boolean(me?.pastorProfile);

  return (
    <div className="pastor-call inner pc-page">
      <div className="pc-page__head">
        <h2>담임목사 청빙 소식</h2>
        <div className="pc-page__tools">
          <button type="button" className="mm-btn mm-btn--primary pc-btn-sm" onClick={() => go(hasProfile ? '/pastor/my' : '/pastor/profile')}>
            {hasProfile ? '내 청빙 소식' : '이력서 맡기고 추천 받기'}
          </button>
        </div>
      </div>

      <section className="pc-sect--flat">
        <div className="mm-sect"><h3>사역자모아에 들어온 청빙 의뢰</h3><span className="sub">이력서를 맡긴 목사님께만 보입니다</span></div>
        {!hasProfile ? (
          <div className="pc-empty">
            <p>공고 없이 사역자모아에 직접 담임목사를 의뢰한 교회들이 있습니다.</p>
            <p>담임목회 희망 이력서를 맡겨 주시면 의뢰 목록을 보실 수 있고, 맞는 교회가 있으면 먼저 추천해 드립니다.</p>
            <button type="button" className="mm-btn mm-btn--primary pc-btn-md" style={{ marginTop: 12 }} onClick={() => go(isLogin ? '/pastor/profile' : '/login')}>
              {isLogin ? '이력서 맡기기' : '로그인하고 이력서 맡기기'}
            </button>
          </div>
        ) : data.requests.length === 0 ? (
          <div className="pc-empty"><p>지금 열려 있는 청빙 의뢰가 없습니다.</p><p>새 의뢰가 들어오면 조건에 맞춰 알려 드립니다.</p></div>
        ) : (
          <ul className="pc-plist">
            {data.requests.map((r) => (
              <li key={r.id} className="pc-pitem">
                <div className="pc-pitem__head">
                  <div>
                    <p className="pc-pitem__title">{r.churchName}</p>
                    <p className="pc-pitem__meta">{[r.denomination, [r.sido, r.sigungu].filter(Boolean).join(' '), r.regionType, r.attendance && `출석 ${r.attendance}`].filter(Boolean).join(' · ')}</p>
                  </div>
                  {r.myStatus && <span className={`pc-status pc-status--${r.myStatus === 'DELIVERED' ? 'accepted' : 'pending'}`}>{DELIVERY_STATUS_LABEL[r.myStatus]}</span>}
                </div>
                <dl className="pc-pitem__rows">
                  {r.intro && <div><dt>교회 소개</dt><dd className="pc-pre">{r.intro}</dd></div>}
                  {r.desiredNote && <div><dt>원하는 목사님</dt><dd className="pc-pre">{r.desiredNote}</dd></div>}
                  {r.offerConditions.length > 0 && <div><dt>제공 조건</dt><dd>{r.offerConditions.join(', ')}</dd></div>}
                  {r.salaryNote && <div><dt>처우</dt><dd>{r.salaryNote}</dd></div>}
                </dl>
                {!r.myStatus && (
                  noteFor === r.id ? (
                    <div className="pc-decline">
                      <textarea className="mm-field" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="교회에 전할 말씀 (선택)" />
                      <div className="pc-pitem__btns">
                        <button type="button" className="mm-btn mm-btn--outline pc-btn-md" onClick={() => setNoteFor(null)}>취소</button>
                        <button type="button" className="mm-btn mm-btn--primary pc-btn-md" disabled={busy} onClick={() => interest(r.id)}>이력서 전달 요청</button>
                      </div>
                      <p className="pc-hint">사역자모아가 확인한 뒤 이 교회에만 목사님의 이력서를 전달합니다.</p>
                    </div>
                  ) : (
                    <div className="pc-pitem__btns">
                      <button type="button" className="mm-btn mm-btn--primary pc-btn-md" onClick={() => setNoteFor(r.id)}>이 교회에 이력서 보내기</button>
                    </div>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="pc-sect--flat">
        <div className="mm-sect"><h3>구인정보의 담임목사 공고</h3><span className="sub">{data.recruits.length}건</span></div>
        {data.recruits.length === 0 ? (
          <div className="pc-empty"><p>지금 올라와 있는 담임목사 공고가 없습니다.</p></div>
        ) : (
          <ul className="pc-plist">
            {data.recruits.map((r) => (
              <li key={`${r.kind}-${r.id}`} className="pc-pitem pc-pitem--link" onClick={() => go(r.path)}>
                <div className="pc-pitem__head">
                  <div>
                    <p className="pc-pitem__title">{r.title}</p>
                    <p className="pc-pitem__meta">{[r.church, r.region, r.pay, r.deadline].filter(Boolean).join(' · ')}</p>
                  </div>
                  <span className="pc-pitem__go">공고 보기 →</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
